import { formatUnits } from 'viem'

export interface TimedPoint {
  timestamp: number
}

/**
 * For point-in-time state series: the first point of each UTC day (midnight, except for a
 * partial first day), plus the last point (Core's "now" row on state history). For daily
 * APY, trim the `thirty_days` rows with `toTrailingDays` instead.
 * Expects ascending points, as Core sends them.
 */
export function toDailySeries<T extends TimedPoint>(points: readonly T[]): T[] {
  const daily: T[] = []
  let previousDay: number | undefined

  for (const point of points) {
    const day = Math.floor(point.timestamp / 86400)
    if (day !== previousDay) {
      daily.push(point)
      previousDay = day
    }
  }

  const last = points.at(-1)
  if (last !== undefined && daily.at(-1) !== last) daily.push(last)

  return daily
}

/** Points within `days` days of the last point, inclusive. Expects ascending points.
 * @internal
 */
export function toTrailingDays<T extends TimedPoint>(points: readonly T[], days: number): T[] {
  const last = points.at(-1)
  if (last === undefined) return []

  const cutoff = last.timestamp - days * 86400
  return points.filter(point => point.timestamp >= cutoff)
}

/** For each point, the latest series point at or before it; null when the series starts after it. `series` need not be sorted. */
export function toNearestEarlier<P extends TimedPoint, S extends TimedPoint>(
  points: readonly P[],
  series: readonly S[]
): Array<S | null> {
  const sorted = [...series].sort((left, right) => left.timestamp - right.timestamp)

  return points.map(point => {
    let low = 0
    let high = sorted.length

    while (low < high) {
      const middle = Math.floor((low + high) / 2)
      const candidate = sorted[middle]
      if (candidate !== undefined && candidate.timestamp <= point.timestamp) low = middle + 1
      else high = middle
    }

    return low === 0 ? null : (sorted[low - 1] ?? null)
  })
}

export interface PricePoint {
  timestamp: number
  price: number | null
}

/**
 * USD value of a raw token amount, as a float for charts, not for exact amounts. A zero amount or a
 * zero price yields zero; a nonzero amount without a price stays null.
 */
export function toUsdValue(amount: bigint, decimals: number, price: number | null): number | null {
  if (amount === 0n) return 0
  return price === null ? null : Number(formatUnits(amount, decimals)) * price
}

/**
 * USD value of a raw token amount at a known price, as a float for charts, not for exact amounts.
 * Null without a price, even for a zero amount. Vault V1 values (position history, action panels,
 * allocations, migration) and the Vault V2 action panel use it, because their contracts show `-`
 * for any unpriced amount; the V1 total deposits and its history value a zero amount at 0
 * (`toUsdValue`, `toSupplyUsdAmounts`), and every other caller uses `toUsdValue`.
 */
export function toPricedUsdValue(
  amount: bigint,
  decimals: number,
  price: number | null
): number | null {
  return price === null ? null : Number(formatUnits(amount, decimals)) * price
}

/** Largest gap between two series' "now" rows that still counts as the same moment; well below the hourly grid step. */
const NOW_SKEW_SECONDS = 60

/**
 * Each upstream stamps its "now" row when it answers, so `series`' can land a few seconds after
 * `points`'. Re-stamping `series`' last row onto `points`' keeps the latest point on the current
 * value instead of the previous grid row. Expects ascending points.
 */
export function alignNow<S extends TimedPoint>(
  points: readonly TimedPoint[],
  series: readonly S[]
): readonly S[] {
  const lastPoint = points.at(-1)
  const lastSeries = series.at(-1)
  if (lastPoint === undefined || lastSeries === undefined) return series

  const skew = lastSeries.timestamp - lastPoint.timestamp
  if (skew <= 0 || skew > NOW_SKEW_SECONDS) return series

  return [...series.slice(0, -1), { ...lastSeries, timestamp: lastPoint.timestamp }]
}

/**
 * Each point's amount in USD at the latest price at or before it, the price series' "now" row
 * aligned onto the points' (see `alignNow`). `null` for a nonzero amount when no price precedes the
 * point or that price is itself `null`, and when the token's decimals are unknown; 0 for a zero
 * amount even without a price.
 */
export function toUsdSeries<P extends TimedPoint>(
  points: readonly P[],
  prices: readonly PricePoint[],
  amountOf: (point: P) => bigint,
  decimals: number | null
): Array<{ timestamp: number; usd: number | null }> {
  const nearestPrices = toNearestEarlier(points, alignNow(points, prices))

  return points.map((point, index) => ({
    timestamp: point.timestamp,
    usd:
      decimals === null
        ? null
        : toUsdValue(amountOf(point), decimals, nearestPrices[index]?.price ?? null)
  }))
}
